/**
 * API Interceptor - Handles 401 errors with automatic token refresh and retry
 *
 * Purpose: When an API returns 401 for a request that carried the platform
 * bearer token, trigger a token refresh, wait for the refreshed token to
 * land in the auth cookie, and retry the request once with the new token.
 *
 * This is TRANSPARENT to existing code - it wraps window.fetch without
 * modifying existing API calls or error handlers. Requests without an
 * Authorization header are never retried.
 */

import { getFreshToken } from "../utils/auth"
import { getCookieValue, isValidToken } from "../utils/cookies"

interface FetchOptions extends RequestInit {
  retryOnUnauthorized?: boolean
  _retryCount?: number
}

const MAX_RETRIES = 1
const TOKEN_POLL_INTERVAL_MS = 500
const TOKEN_POLL_TIMEOUT_MS = 8000
const REFRESH_THROTTLE_MS = 5000

let installed = false
let originalFetchRef: typeof window.fetch | null = null
// Coordinate concurrent 401s: when a burst of in-flight requests all get 401
// on an expired token, only the first should trigger getFreshToken() — the
// rest poll the cookie for the token the first refresh produces. Without this
// N requests spawned N auth-iframe cycles.
let lastRefreshTriggeredAt = 0

/**
 * Read the current bearer token from the auth cookie.
 * The oidc.auth cookie holds base64-encoded JSON: { token, expiry }.
 * Returns null if the cookie is missing, unparsable, or expired.
 */
function readTokenFromCookie(): string | null {
  try {
    const authCookie = getCookieValue("oidc.auth")
    if (!isValidToken(authCookie)) {
      return null
    }
    const decoded = JSON.parse(atob(authCookie))
    if (new Date(decoded.expiry).getTime() <= Date.now()) {
      return null
    }
    return decoded.token || null
  } catch {
    return null
  }
}

function getAuthHeader(
  input: RequestInfo | URL,
  init?: RequestInit
): string | null {
  try {
    // new Headers() normalizes every HeadersInit shape — Headers instance,
    // plain object, AND tuple arrays ([["Authorization", "..."]]), which
    // object indexing silently missed. Fall back to the Request's own
    // headers when no init headers were passed.
    if (init?.headers) {
      return new Headers(init.headers).get("Authorization")
    }
    if (typeof Request !== "undefined" && input instanceof Request) {
      return input.headers.get("Authorization")
    }
    return null
  } catch {
    return null
  }
}

function withAuthHeader(
  init: FetchOptions | undefined,
  token: string
): FetchOptions {
  const newInit: FetchOptions = { ...(init || {}) }
  const headers = new Headers(newInit.headers || undefined)
  headers.set("Authorization", `Bearer ${token}`)
  newInit.headers = headers
  return newInit
}

/**
 * Poll the auth cookie until it contains a valid token different from the
 * stale one, or give up after TOKEN_POLL_TIMEOUT_MS.
 */
function waitForRefreshedToken(
  staleToken: string | null
): Promise<string | null> {
  return new Promise((resolve) => {
    const startedAt = Date.now()
    const poll = () => {
      const token = readTokenFromCookie()
      if (token && token !== staleToken) {
        resolve(token)
        return
      }
      if (Date.now() - startedAt >= TOKEN_POLL_TIMEOUT_MS) {
        resolve(null)
        return
      }
      setTimeout(poll, TOKEN_POLL_INTERVAL_MS)
    }
    poll()
  })
}

/**
 * Install the API interceptor
 * Must be called early in app initialization, before any API calls
 * This is non-destructive - it wraps the existing fetch, not replaces it.
 * Idempotent: repeated calls are no-ops.
 */
export function installApiInterceptor(): void {
  if (installed) {
    return
  }
  installed = true
  const originalFetch = window.fetch
  originalFetchRef = originalFetch

  window.fetch = function interceptedFetch(
    input: RequestInfo | URL,
    init?: FetchOptions
  ): Promise<Response> {
    const shouldRetry = init?.retryOnUnauthorized !== false
    const retryCount = init?._retryCount || 0

    return originalFetch.call(window, input, init).then(async (response) => {
      if (
        response.status !== 401 ||
        !shouldRetry ||
        retryCount >= MAX_RETRIES
      ) {
        return response
      }

      // Only retry requests that carried a bearer token — anything else
      // (public assets, non-platform auth) is left untouched.
      const authHeader = getAuthHeader(input, init)
      if (!authHeader || !authHeader.startsWith("Bearer ")) {
        return response
      }
      const staleToken = authHeader.slice("Bearer ".length)

      try {
        let freshToken = readTokenFromCookie()
        if (!freshToken || freshToken === staleToken) {
          // The platform token itself is expired/stale. Only the first 401 in
          // a burst triggers the refresh (throttled); concurrent 401s just
          // wait for the token that refresh produces.
          if (Date.now() - lastRefreshTriggeredAt > REFRESH_THROTTLE_MS) {
            lastRefreshTriggeredAt = Date.now()
            console.log("[APIInterceptor] Got 401, triggering token refresh")
            getFreshToken()
          } else {
            console.log("[APIInterceptor] Got 401, refresh already in flight — waiting")
          }
          freshToken = await waitForRefreshedToken(staleToken)
        }

        if (!freshToken) {
          console.warn(
            "[APIInterceptor] Token refresh did not complete, returning 401"
          )
          return response
        }

        console.log("[APIInterceptor] Retrying request with refreshed token")
        const newInit: FetchOptions = {
          ...withAuthHeader(init, freshToken),
          _retryCount: retryCount + 1
        }
        return originalFetch.call(window, input, newInit)
      } catch (error) {
        console.error("[APIInterceptor] Retry failed:", error)
        // Return original 401 response if retry fails
        return response
      }
    })
  }

  console.log("[APIInterceptor] Installed at window.fetch level")
}

/**
 * Remove the API interceptor (mainly for testing)
 */
export function uninstallApiInterceptor(): void {
  if (installed && originalFetchRef) {
    window.fetch = originalFetchRef
    originalFetchRef = null
    installed = false
    console.log("[APIInterceptor] Uninstalled")
  }
}
